import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";

type Kind = "currency" | "integer" | "boolean" | "select" | "text";
interface KeySpec {
  key: string;
  label: string;
  description: string;
  kind: Kind;
  options?: { value: string; label: string }[];
  prefix?: string;
  suffix?: string;
}

const KEYS: KeySpec[] = [
  {
    key: "payroll_skip_threshold",
    label: "Payroll skip threshold",
    description: "Reimbursement claims at or below this amount skip the payroll review (manager-only). Set to 0 to always require payroll.",
    kind: "currency",
    prefix: "£",
  },
  {
    key: "receipt_required_threshold",
    label: "Receipt required above",
    description: "Lines above this amount must have at least one receipt attached. Lines at or below this can submit without one.",
    kind: "currency",
    prefix: "£",
  },
  {
    key: "closed_period_days",
    label: "Closed period (days)",
    description: "Block submission of expenses with a date older than this many days. Soft-block — admin can override.",
    kind: "integer",
    suffix: "days",
  },
  {
    key: "mileage_tier_active",
    label: "HMRC mileage tier active",
    description: "When ON, mileage above the tier threshold drops to the lower rate (HMRC 45p → 25p at 10k miles). When OFF, single flat rate is used (Liaise standard).",
    kind: "boolean",
  },
  {
    key: "hmrc_tier_threshold_miles",
    label: "HMRC tier threshold",
    description: "Per-claimant tax-year miles above which the lower HMRC rate kicks in. Only used when the tier is active.",
    kind: "integer",
    suffix: "miles",
  },
  {
    key: "weekly_pay_day",
    label: "Pay day",
    description: "Day of the week that the weekly pay run is processed.",
    kind: "select",
    options: [
      { value: "monday", label: "Monday" },
      { value: "tuesday", label: "Tuesday" },
      { value: "wednesday", label: "Wednesday" },
      { value: "thursday", label: "Thursday" },
      { value: "friday", label: "Friday" },
    ],
  },
  {
    key: "weekly_cutoff_day",
    label: "Submission cut-off",
    description: "Day of the week by which manager approval must be in to be paid in the next run.",
    kind: "select",
    options: [
      { value: "sunday", label: "Sunday (current Liaise rule)" },
      { value: "monday", label: "Monday" },
      { value: "tuesday", label: "Tuesday" },
      { value: "wednesday", label: "Wednesday" },
      { value: "thursday", label: "Thursday" },
      { value: "friday", label: "Friday" },
      { value: "saturday", label: "Saturday" },
    ],
  },
  {
    key: "distance_provider",
    label: "Distance provider",
    description: "Service used to compute postcode-to-postcode mileage when the claimant doesn't enter the distance manually.",
    kind: "select",
    options: [
      { value: "azure_maps", label: "Azure Maps" },
      { value: "google", label: "Google Distance Matrix" },
      { value: "manual", label: "Manual only (no auto-distance)" },
    ],
  },
];

function PolicyRow({ spec, value, onSave }: { spec: KeySpec; value: string; onSave: (v: string) => Promise<void> }) {
  const [v, setV] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = v !== (value ?? "");

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(v);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-liaise-navy">{spec.label}</div>
          <div className="text-slate-600 text-sm mt-1">{spec.description}</div>
        </div>
        <div className="flex items-center gap-2">
          {spec.kind === "boolean" ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={v === "true"}
                onChange={(e) => setV(e.target.checked ? "true" : "false")}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-600">{v === "true" ? "Enabled" : "Disabled"}</span>
            </label>
          ) : spec.kind === "select" ? (
            <select
              value={v}
              onChange={(e) => setV(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {spec.options?.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-1">
              {spec.prefix && <span className="text-slate-500 text-sm">{spec.prefix}</span>}
              <input
                type={spec.kind === "currency" || spec.kind === "integer" ? "number" : "text"}
                step={spec.kind === "currency" ? "0.01" : "1"}
                value={v}
                onChange={(e) => setV(e.target.value)}
                className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm text-right"
              />
              {spec.suffix && <span className="text-slate-500 text-sm">{spec.suffix}</span>}
            </div>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-liaise-navy text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-40 transition"
          >
            {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
          </button>
        </div>
      </div>
      {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
    </div>
  );
}

export default function PolicyAdmin() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["admin-policy"], queryFn: api.admin.listPolicy });

  const upsert = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => api.admin.setPolicy(key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-policy"] }),
  });

  const valueFor = (key: string) => data?.find((r) => r.key === key)?.value ?? "";

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Policy</h1>
        <p className="text-slate-600 mt-1">Thresholds and rules that govern submission, approval, and payment.</p>
      </div>

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          <div className="font-medium">Failed to load policy</div>
          <div className="font-mono text-xs mt-1">{(error as Error).message}</div>
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {KEYS.map((spec) => (
            <PolicyRow
              key={spec.key}
              spec={spec}
              value={valueFor(spec.key)}
              onSave={async (v) => { await upsert.mutateAsync({ key: spec.key, value: v }); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
